import React, { useState, useEffect } from 'react';
import Network from '../../../models/Network.js';
import URLS from '../../../urls.js';
import './Module.css';
import LoadingListElement from '../list_elements/LoadingListElement.js';
import SettingsGridElement from '../list_elements/SettingsGridElement.js';

const AgencySettingsModule = ({ user, isSmallScreen, testSections = ['info', 'check', 'measure', 'narrative-suggestion'] }) => {
    const [settingsJson, setSettingsJson] = useState(null);
    const [allTestMetadata, setAllTestMetadata] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState(null);

    const getUserAgencies = async (uid) => {
        const url = URLS.GET_USER_AGENCIES_URL;
        const dict = {
            uid: uid,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        try {
            await Network.post(url, dict, (response) => {
                if (Network.checkValidStatusAndResponse(response)) {
                    const agenciesData = response.data;
                    setAgencies(agenciesData);
                    console.log('agenciesData: ' + JSON.stringify(agenciesData, null, 2));
                    if (agenciesData.length > 0) {
                        setSelectedAgency(agenciesData[0]);
                        // Load settings for the first agency
                        if (agenciesData[0].settings_json) {
                            setSettingsJson(JSON.parse(agenciesData[0].settings_json));
                        } else {
                            setSettingsJson({
                                enabledTests: [],
                                enabledSuggestions: []
                            });
                        }
                    }
                } else {
                    console.log("Failed to fetch agencies");
                }
            });
        } catch (error) {
            console.log('Error fetching agencies:', error);
        }
    };

    const getAllTestMetadata = async () => {
        const url = URLS.GET_ALL_TEST_METADATA_URL;
        const dict = {
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        try {
            await Network.post(url, dict, (response) => {
                if (Network.checkValidStatusAndResponse(response)) {
                    setAllTestMetadata(response.data);
                    console.log('Test metadata loaded:', response.data.length, 'items');
                } else {
                    console.log("Failed to fetch test metadata");
                }
            });
        } catch (error) {
            console.log('Error fetching test metadata:', error);
        }
    };

    const handleSettingsUpdate = async (newSettings) => {
        if (!selectedAgency) return;

        const url = URLS.AGENCY_SETTINGS_UPDATE_URL;
        const dict = {
            agency_uid: selectedAgency.uid,
            settingsJson: newSettings,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        try {
            await Network.post(url, dict, (response) => {
                if (Network.checkValidStatusAndResponse(response)) {
                    // Refresh agencies to get updated settings
                    getUserAgencies(user.uid);
                } else {
                    console.log("Failed to update settings");
                }
            });
        } catch (error) {
            console.log('Error updating settings:', error);
        }
    };

    const handleAgencyChange = (agency) => {
        setSelectedAgency(agency);
        if (agency.settings_json) {
            setSettingsJson(JSON.parse(agency.settings_json));
        } else {
            setSettingsJson({
                enabledTests: [],
                enabledSuggestions: []
            });
        }
    };

    const handleTestToggle = (testId) => {
        const newSettings = { ...settingsJson };
        const index = newSettings.enabledTests.indexOf(testId);
        
        if (index === -1) {
            // Test is not enabled, add it
            newSettings.enabledTests = [...newSettings.enabledTests, testId];
        } else {
            // Test is enabled, remove it
            newSettings.enabledTests = newSettings.enabledTests.filter(id => id !== testId);
        }
        
        setSettingsJson(newSettings);
    };

    const handleEnableAll = () => {
        const newSettings = { ...settingsJson };
        newSettings.enabledTests = allTestMetadata.map(test => test.test_metadata_id);
        setSettingsJson(newSettings);
    };

    const handleDisableAll = () => {
        const newSettings = { ...settingsJson };
        newSettings.enabledTests = [];
        setSettingsJson(newSettings);
    };

    useEffect(() => {
        if (user?.uid) {
            getUserAgencies(user.uid);
            getAllTestMetadata();
        }
    }, [user.uid]);

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <div className="list-header">
                {agencies.length > 0 && (
                    <>
                        {agencies.length === 1 ? (
                            <div style={{
                                marginRight: '1rem',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                color: '#FFF'
                            }}>
                                {selectedAgency?.agency_name || selectedAgency?.agency_number}
                            </div>
                        ) : (
                            <select 
                                value={selectedAgency?.uid}
                                onChange={(e) => handleAgencyChange(agencies.find(a => a.uid === parseInt(e.target.value)))}
                                style={{
                                    marginRight: '1rem',
                                    padding: '0.5rem',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                }}
                            >
                                {agencies.map(agency => (
                                    <option key={agency.uid} value={agency.uid}>
                                        {agency.agency_name || agency.agency_number}
                                    </option>
                                ))}
                            </select>
                        )}
                    </>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {testSections.includes('all') && (
                        <button 
                            onClick={handleEnableAll}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Enable All
                        </button>
                    )}
                    {/* <button 
                        onClick={handleDisableAll}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Disable All
                    </button> */}
                    <button 
                        onClick={() => handleSettingsUpdate(settingsJson)}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Save Settings
                    </button>
                </div>
            </div>
            
            {/* Explanatory text based on current tab */}
            {settingsJson && (
                <div style={{ 
                    padding: '1rem 1rem 0 1rem',
                    fontSize: '0.9rem',
                    color: '#666',
                    fontStyle: 'italic'
                }}>
                    {testSections.includes('info') && testSections.length === 1 ? (
                        "Disabling a tag will prevent it from being applied to individual reports and disable downstream tests by default."
                    ) : testSections.length === 1 ? (
                        ""
                    ) : (
                        ""
                    )}
                </div>
            )}
            
            {!settingsJson ? (
                <LoadingListElement />
            ) : (
                <div style={{ padding: '1rem' }}>
                    <div style={{ marginBottom: '2rem' }}>
                        {testSections.map((section) => {
                            return (
                                <div key={section} style={{ marginBottom: '2rem' }}>
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                        gap: '1rem'
                                    }}>
                                        {allTestMetadata
                                            .filter(test => section === 'all' || test.impact_attribute === section)
                                            .sort((a, b) => a.test_metadata_id - b.test_metadata_id)
                                            .map((test) => (
                                                <SettingsGridElement
                                                    key={test.test_metadata_id}
                                                    test={test}
                                                    isEnabled={settingsJson.enabledTests.includes(test.test_metadata_id)}
                                                    onToggle={handleTestToggle}
                                                />
                                            ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgencySettingsModule; 